import Stardog, {
  Connection as StardogConnection,
  db as StardogDb,
  server as StardogServer,
  user as StardogUser,
} from 'stardog';
import { BaseConnection, getStardogConnection } from 'vet-bones/utils';

import { Connection as GraphConnection } from 'src/ui/graph/types';
import { runWithRefetchConnectionToken } from 'src/ui/hooks/refetchConnectionToken';

export const CATALOG_DB_SERVER_PROPERTY = 'catalog.database';
export const CATALOG_DB_DEFAULT = 'catalog';
export const QUERYLOG_DB_SERVER_PROPERTY = 'dbms.query.log.database';
export const QUERYLOG_DB_DEFAULT = 'querylog';

/**
 * For any databases we create, these are the known set of options that provide the best
 * experience with our tooling.
 */
export const EXPLORER_DB_OPTIONS = {
  // for optimal Explorer experience
  search: { enabled: true },

  // for the marketplace
  graph: { aliases: true },
};

const filterUserDefinedDatabases = async (
  connection: GraphConnection,
  stardogConnection: StardogConnection,
  allDatabases: string[]
): Promise<string[]> => {
  let catalogDb = CATALOG_DB_DEFAULT;
  let querylogDb = QUERYLOG_DB_DEFAULT;

  try {
    // TODO: This should maybe be cached? React query could maybe help with this somehow?
    const serverProps = await runWithRefetchConnectionToken()(
      stardogConnection,
      connection.index,
      (stardogConn) =>
        StardogServer.properties(stardogConn, [
          CATALOG_DB_SERVER_PROPERTY,
          QUERYLOG_DB_SERVER_PROPERTY,
        ])
    );
    catalogDb =
      serverProps.body[CATALOG_DB_SERVER_PROPERTY] ?? CATALOG_DB_DEFAULT;
    querylogDb =
      serverProps.body[QUERYLOG_DB_SERVER_PROPERTY] ?? QUERYLOG_DB_DEFAULT;
  } catch (e) {
    console.warn(e);
  }

  return allDatabases.filter((db) => db !== catalogDb && db !== querylogDb);
};

/**
 * Lists the names of databases on the server associated with the provided connection
 */
export const listDatabases = async (
  connection: GraphConnection | null | undefined,
  options = {
    userDefinedOnly: false,
  }
): Promise<string[]> => {
  if (!connection || connection.isAllocating) {
    return [];
  }

  try {
    const stardogConnection = getStardogConnection(
      connection as BaseConnection,
      { agent: 'Portal' }
    );

    const databaseResponse = await runWithRefetchConnectionToken()(
      stardogConnection,
      connection.index,
      (stardogConn) => StardogDb.list(stardogConn)
    );
    if (!databaseResponse.ok) {
      throw new Error(databaseResponse.statusText);
    }

    if (options.userDefinedOnly) {
      return filterUserDefinedDatabases(
        connection,
        stardogConnection,
        databaseResponse.body?.databases || []
      );
    }

    return databaseResponse.body?.databases || [];
  } catch (e) {
    console.warn(e);
  }

  return [];
};

/** Parameters for permission check */
export interface CheckPermissionParams {
  action: string;
  resourceType: string;
  resource: string;
}

/**
 * Check whether the current connection has a specific permission.
 * Uses stardog.js user.checkPermission(conn, action, resourceType, resource).
 */
export const checkPermission = async (
  connection: GraphConnection | null | undefined,
  org: string,
  params: CheckPermissionParams
): Promise<boolean> => {
  if (!connection || connection.isAllocating) {
    return false;
  }

  try {
    const stardogConnection = getStardogConnection(
      connection as BaseConnection,
      { agent: 'Portal' }
    );

    const result = await runWithRefetchConnectionToken(org)(
      stardogConnection,
      connection.index,
      (stardogConn) =>
        StardogUser.checkPermission(
          stardogConn,
          params.action as StardogUser.Action,
          params.resourceType as StardogUser.ResourceType,
          params.resource
        )
    );

    const body = result?.body as { hasPermission?: boolean } | undefined;
    return body?.hasPermission === true;
  } catch (e) /* istanbul ignore next: no need to test defensive code */ {
    console.warn(e);
    return false;
  }
};

/**
 * Create a database on the server
 */
export const create = async (
  connection: GraphConnection,
  db: string,
  options: any
): Promise<boolean> => {
  try {
    const stardogConnection = getStardogConnection(
      connection as BaseConnection,
      { agent: 'Portal' }
    );

    return runWithRefetchConnectionToken()(
      stardogConnection,
      connection.index,
      (stardogConn) => StardogDb.create(stardogConn, db, options)
    ).then((r) => r.ok);
  } catch (e) /* istanbul ignore next: no need to test defensive code */ {
    console.warn(e);
    return Promise.resolve(false);
  }
};

/**
 * Add the given content to the database
 */
export const add = async (
  connection: GraphConnection,
  db: string,
  data: string,
  contentType: Stardog.HTTP.RdfMimeType
): Promise<boolean> => {
  try {
    const stardogConnection = getStardogConnection(
      connection as BaseConnection,
      { agent: 'Portal' }
    );

    return runWithRefetchConnectionToken()(
      stardogConnection,
      connection.index,
      (stardogConn) => StardogDb.transaction.begin(stardogConn, db)
    ).then((response) => {
      if (!response.ok) {
        return false;
      }

      return runWithRefetchConnectionToken()(
        stardogConnection,
        connection.index,
        (stardogConn) =>
          StardogDb.add(stardogConn, db, response.transactionId, data, {
            // @ts-ignore working around an issue in stardog.js
            encoding: undefined,
            contentType,
          })
      ).then((results) => {
        if (!results.ok) {
          // what if this fails? :grimacing: -- result is always false because the tx failed, even if rollback is fine
          return runWithRefetchConnectionToken()(
            stardogConnection,
            connection.index,
            (stardogConn) =>
              StardogDb.transaction.rollback(
                stardogConn,
                db,
                response.transactionId
              )
          ).then(() => false);
        }

        return runWithRefetchConnectionToken()(
          stardogConnection,
          connection.index,
          (stardogConn) =>
            StardogDb.transaction.commit(
              stardogConn,
              db,
              response.transactionId
            )
        ).then((r) => {
          if (!r.ok) {
            return runWithRefetchConnectionToken()(
              stardogConnection,
              connection.index,
              (stardogConn) =>
                StardogDb.transaction.rollback(
                  stardogConn,
                  db,
                  response.transactionId
                )
            ).then(() => false);
          }
          return r.ok;
        });
      });
    });
  } catch (e) /* istanbul ignore next: no need to test defensive code */ {
    console.warn(e);
    return Promise.resolve(false);
  }
};
